// formDraftStore — the VIEW-EPHEMERAL, REQUEST-ID-KEYED home for an in-progress
// elicitation-form draft (F94/F96). Mirrors contextCardFold.ts / tourSeen.ts:
// a pure parse/serialize + pure-transition layer over the SINGLE localStorage
// seam (deviceStore), keyed per request at `bilda.formDraft.<requestId>`.
//
// WHY IT EXISTS (the root of F94 + F96): `InformationRequestCard` held its draft
// in component-local `useState`, whose lifetime is bound to the React component
// INSTANCE, not to the elicitation request. Any unmount/remount of the card
// (e.g. the compact transcript split moving the form entry across the
// last-author-turn boundary into the conditionally-rendered "earlier" subtree,
// or a full page reload) discarded the draft — so a field "couldn't be changed"
// (it reset the instant the card remounted) and a filled form was wiped on
// reload. Binding the draft's lifetime to the REQUEST ID (stable across remount
// AND reload) fixes both with one seam: the card seeds from `readFormDraft` and
// writes through `writeFormDraft`, so a remount RECOVERS the draft rather than
// resetting it, and a reload restores it.
//
// STILL VIEW-EPHEMERAL, NEVER DOMAIN STATE (UI-as-projection): a draft is not a
// fact about the past until the author SUBMITS it — only then does it cross into
// the event log as `supply_information`. So it lives in the device stratum
// (localStorage, outside the fold — project_theme_outside_event_log), like unsent
// composer text, and is CLEARED (`clearFormDraft`) the moment the request is
// answered or skipped. The pure layer degrades any legacy/foreign shape to
// `emptyFormDraft` (parse, don't validate).

import {
    readDeviceKey,
    removeDeviceKey,
    writeDeviceKey,
} from "./deviceStore.js";

// The per-field draft, split by value shape so every read is cast-free: single-
// valued controls (text, single-choice, the scale's numeric string) live in
// `single`; multi-choice selections in `multi`. Keyed by field key, which is
// unique across ALL steps, so one draft spans the whole form. This is the SAME
// shape `InformationRequestCard` renders from — defined here so the card, the
// store, and its test read one definition.
export type FormDraft = {
    readonly single: Readonly<Record<string, string>>;
    readonly multi: Readonly<Record<string, readonly string[]>>;
};

// The current question is NOT part of the draft: it is view-ephemeral (D42) and
// reopens on the first unanswered question. A legacy draft's `step` is ignored.
export const emptyFormDraft: FormDraft = { single: {}, multi: {} };

// --- Pure transitions --------------------------------------------------------
// The two ways a control mutates the draft, as pure functions (not inline in the
// component) so the card AND the test drive the SAME logic.

export const setDraftSingle = (
    draft: FormDraft,
    key: string,
    value: string,
): FormDraft => ({ ...draft, single: { ...draft.single, [key]: value } });

export const toggleDraftMulti = (
    draft: FormDraft,
    key: string,
    value: string,
): FormDraft => {
    const current = draft.multi[key] ?? [];
    const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
    return { ...draft, multi: { ...draft.multi, [key]: next } };
};

// --- Pure parse / serialize --------------------------------------------------

const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);

// Keep only string→string pairs (a foreign/legacy shape drops the offending key,
// never the whole draft).
const stringRecord = (raw: unknown): Readonly<Record<string, string>> => {
    if (!isRecord(raw)) return {};
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(raw)) {
        if (typeof value === "string") out[key] = value;
    }
    return out;
};

// Keep only string→string[] pairs; within each array keep only strings.
const stringListRecord = (
    raw: unknown,
): Readonly<Record<string, readonly string[]>> => {
    if (!isRecord(raw)) return {};
    const out: Record<string, readonly string[]> = {};
    for (const [key, value] of Object.entries(raw)) {
        if (Array.isArray(value)) {
            out[key] = value.filter((v): v is string => typeof v === "string");
        }
    }
    return out;
};

// Parse, don't validate: the string may have been written by ANY earlier build.
// Keep only the known shape; anything else degrades to `emptyFormDraft`.
export const parseFormDraft = (raw: string | null): FormDraft => {
    if (raw === null) return emptyFormDraft;
    let data: unknown;
    try {
        data = JSON.parse(raw);
    } catch {
        return emptyFormDraft;
    }
    if (!isRecord(data)) return emptyFormDraft;
    return {
        single: stringRecord(data.single),
        multi: stringListRecord(data.multi),
    };
};

export const serializeFormDraft = (draft: FormDraft): string =>
    JSON.stringify({ single: draft.single, multi: draft.multi });

// --- Impure seam (host-style) ------------------------------------------------
// The ONLY impurity — routed through deviceStore, the single localStorage seam.
// Called host-style from the card's mount initializer + its change handlers
// (mirroring contextCardFold's read/write), never from a buried effect.

const keyFor = (requestId: string): string => `bilda.formDraft.${requestId}`;

export const readFormDraft = (requestId: string): FormDraft =>
    parseFormDraft(readDeviceKey(keyFor(requestId)));

export const writeFormDraft = (requestId: string, draft: FormDraft): void => {
    writeDeviceKey(keyFor(requestId), serializeFormDraft(draft));
};

export const clearFormDraft = (requestId: string): void => {
    removeDeviceKey(keyFor(requestId));
};
